import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './MobileGameDetail.module.css';
import { useGameContext } from '../../context/GameContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import HiddenText from '../HiddenText';
import { formatGameDateTime } from '../../utils/timeUtils';
import { formatLine } from '../../utils/lineDataUtils';

// Define the SportsFootballIcon component here
const SportsFootballIcon = () => (
  <svg className={styles.possessionIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
    <path d="M3.02 15.62c-.08 2.42.32 4.34.67 4.69s2.28.76 4.69.67l-5.36-5.36zM13.08 3.28c-2.33.42-4.79 1.34-6.62 3.18s-2.76 4.29-3.18 6.62l7.63 7.63c2.34-.41 4.79-1.34 6.62-3.18s2.76-4.29 3.18-6.62l-7.63-7.63zM9.9 15.5l-1.4-1.4 5.6-5.6 1.4 1.4-5.6 5.6zm11.08-7.12c.08-2.42-.32-4.34-.67-4.69s-2.28-.76-4.69-.67l5.36 5.36z"/>
  </svg>
);

const getPeriodSuffix = (periodNum) => {
  switch (periodNum) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatGamePeriod = (clock, period) => {
  const periodNum = parseInt(period, 10);

  if (clock === "00:00") {
    switch (periodNum) {
      case 1: return "End of 1st";
      case 2: return "Halftime";
      case 3: return "End of 3rd";
      case 4: return "End of 4th";
      default: return "Final";
    }
  }

  const periodSuffix = getPeriodSuffix(periodNum);
  return `${clock} ${periodNum}${periodSuffix}`;
};

const MobileGameDetail = ({
  game,
  displayedWeek,
  isOpen,
  onClose,
  onPickAction,
  hasReachedMaxPicks
}) => {
  const { locksCache, user, isFutureWeekLockAllowed } = useGameContext();
  const { currentWeek } = useCurrentWeek();
  const { canSeeLockers, canSeeLocks } = useLeagueSettings();
  const [selectedPick, setSelectedPick] = useState(null);
  const [futureWeekLockAllowed, setFutureWeekLockAllowed] = useState(true);
  const [isCheckingFutureWeek, setIsCheckingFutureWeek] = useState(false);

  // Helper functions - define these before useMemo hooks
  const calculateMargin = (pickType, lockLine, homeScore, awayScore) => {
    const totalScore = homeScore + awayScore;
    switch(pickType) {
      case 'home':
        return (homeScore - awayScore) + lockLine;
      case 'away':
        return (awayScore - homeScore) + lockLine;
      case 'over':
        return totalScore - lockLine;
      case 'under':
        return lockLine - totalScore;
      default:
        return 0;
    }
  };

  const getMarginColor = (margin) => {
    if (margin > 0) return styles.winning;
    if (margin < 0) return styles.losing;
    return styles.push;
  };

  const getPickLabel = (pickType, game) => {
    switch (pickType) {
      case 'home': return game.home_team.abbreviation || game.home_team.name;
      case 'away': return game.away_team.abbreviation || game.away_team.name;
      case 'over': return 'Over';
      case 'under': return 'Under';
      default: return '';
    }
  };

  const formatLockLine = (lockLine, isOverUnder) => {
    if (isOverUnder) return lockLine;
    return lockLine >= 0 ? `+${lockLine}` : `${lockLine}`;
  };

  const gameLocks = useMemo(() => {
    return locksCache[displayedWeek]?.filter(lock => lock.gameId === game.id) || [];
  }, [locksCache, displayedWeek, game.id]);

  // Calculate margins for lock status bars
  const gameLocksWithMargins = useMemo(() => {
    return gameLocks.map(lock => {
      const margin = calculateMargin(lock.pickType, lock.lockLine, game.home_score || 0, game.away_score || 0);
      return {
        ...lock,
        margin
      };
    }).sort((a, b) => {
      const pickOrder = { away: 0, home: 1, over: 2, under: 3 };
      return pickOrder[a.pickType] - pickOrder[b.pickType];
    });
  }, [gameLocks, game.home_score, game.away_score]);

  const { displayDate, displayTime } = formatGameDateTime(game.start_date);

  // Determine if game is in progress or completed (same logic as desktop GameCard)
  const isGameLockable = useMemo(() => {
    // Game is only lockable if it's scheduled (not in progress or completed)
    return game.game_status === 'scheduled';
  }, [game.game_status]);

  // Check if specific pick types are already locked by other users
  const isPickLockedByOther = useCallback((pickType) => {
    // Only show lock status if locks are visible or user has reached max picks
    if (!canSeeLocks() && !hasReachedMaxPicks) {
      return false;
    }
    return gameLocks.some(lock =>
      lock.pickType === pickType &&
      lock.status === 'locked'
    );
  }, [gameLocks, canSeeLocks, hasReachedMaxPicks]);

  // Check if future week locks are allowed for this week
  useEffect(() => {
    const checkFutureWeekLock = async () => {
      setIsCheckingFutureWeek(true);
      try {
        const allowed = await isFutureWeekLockAllowed(displayedWeek);
        setFutureWeekLockAllowed(allowed);
      } catch (error) {
        console.error('Error checking future week lock allowance:', error);
        setFutureWeekLockAllowed(false);
      } finally {
        setIsCheckingFutureWeek(false);
      }
    };

    checkFutureWeekLock();
  }, [displayedWeek, isFutureWeekLockAllowed]);

  // Helper function to format betting lines with N/A for missing data
  const formatBettingLine = useCallback((value) => {
    if (value === null || value === undefined || value === 'null') {
      return 'N/A';
    }
    // If the value is 0 (pick 'em), also show as N/A when betting data is missing
    if (value === 0) {
      return 'N/A';
    }
    return formatLine(value);
  }, []);

  const handlePickSelect = useCallback((pickType, lockLine) => {
    setSelectedPick({ pickType, lockLine });
  }, []);

  const handleConfirmPick = useCallback((action) => {
    if (selectedPick) {
      onPickAction({
        gameId: game.id,
        pickType: selectedPick.pickType,
        lockLine: selectedPick.lockLine,
        action // 'watch' or 'lock'
      });
      setSelectedPick(null);
      onClose();
    }
  }, [selectedPick, game.id, onPickAction, onClose]);

  const handleClose = useCallback(() => {
    setSelectedPick(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  // Check if lock button should be disabled
  const isLockDisabled = !selectedPick ||
                        hasReachedMaxPicks ||
                        !futureWeekLockAllowed ||
                        isCheckingFutureWeek ||
                        !isGameLockable; // Add game status check

  // Determine if this is a future week based on the actual current week
  // If futureWeekLockAllowed is false, then this is a future week
  const isFutureWeek = !futureWeekLockAllowed && displayedWeek > 1; // Week 1 is always allowed

  // Debug logging
  console.log('MobileGameDetail Debug:', {
    displayedWeek,
    currentWeek,
    isFutureWeek,
    futureWeekLockAllowed,
    isCheckingFutureWeek,
    selectedPick: !!selectedPick,
    hasReachedMaxPicks,
    isLockDisabled,
    gameStatus: game.game_status,
    isGameLockable
  });

  return (
    <>
      {/* Backdrop layer that covers the entire screen */}
      <button
        className={styles.modalBackdrop}
        onClick={handleClose}
        onKeyDown={(e) => e.key === 'Escape' && handleClose()}
        aria-label="Close modal"
      />

      {/* Modal overlay */}
      <div
        className={styles.modalOverlay}
      >
        <dialog
          className={styles.modalContent}
          open={isOpen}
          onClose={handleClose}
          aria-labelledby="game-detail-title"
          aria-describedby="game-detail-description"
        >
        <div className={styles.modalHeader}>
          <h2 id="game-detail-title">
            <span className={styles.team}>
              {game.away_team.rank && <span className={styles.rank}>#{game.away_team.rank}</span>}
              {game.away_team.name}
            </span>
            <span className={styles.vs}> @ </span>
            <span className={styles.team}>
              {game.home_team.name}
              {game.home_team.rank && <span className={styles.rank}>#{game.home_team.rank}</span>}
            </span>
          </h2>
          <button className={styles.closeButton} onClick={handleClose} aria-label="Close game details">×</button>
        </div>

        <div id="game-detail-description" className={styles.gameDetails}>
                      <div className={styles.dateTime}>
              {displayDate} • {displayTime} • {game.tv}
            </div>

            {/* Venue Information */}
            {game.venue_name && (
              <div className={styles.venueInfo}>
                <span className={styles.venueIcon}>🏟️</span>
                <span>
                  {game.venue_name}
                  {game.venue_city && game.venue_state && ` • ${game.venue_city}, ${game.venue_state}`}
                  {game.neutral_site && <span className={styles.neutralSite}> • Neutral Site</span>}
                </span>
              </div>
            )}

            {/* Live Game Status for In-Progress Games */}
            {game.game_status === 'in_progress' && (
              <div className={styles.liveGameInfo}>
                <div className={styles.liveIndicator}>
                  <span className={styles.liveDot}>●</span>
                  LIVE • {formatGamePeriod(game.clock, game.period)}
                </div>
                {game.situation && (
                  <div className={styles.situation}>{game.situation}</div>
                )}
                {game.possession && (
                  <div className={styles.possession}>
                    Possession: {game.possession === 'home' ? game.home_team.name : game.away_team.name}
                    <SportsFootballIcon />
                  </div>
                )}
                {game.last_play && (
                  <div className={styles.lastPlay}>{game.last_play}</div>
                )}
              </div>
            )}

            {/* Weather Information */}
            {game.weather_condition && (
              <div className={styles.weatherInfo}>
                <span className={styles.weatherIcon}>🌤️</span>
                <span>
                  {game.weather_condition}
                  {game.temperature && ` • ${game.temperature}°F`}
                  {game.wind_speed && ` • Wind: ${game.wind_speed} mph`}
                  {game.game_indoors && <span className={styles.indoorGame}> • Indoor</span>}
                </span>
              </div>
            )}

                        <div className={styles.bettingLines}>
              <div className={styles.spreadLine}>
                Spread: {formatBettingLine(game.betting.spread)}
              </div>
              <div className={styles.totalLine}>
                Total: {formatBettingLine(game.betting.over_under)}
              </div>
            </div>
          </div>

          {/* Team Information Section */}
          <div className={styles.teamInformation}>
            <h3>Team Information</h3>

            {/* Away Team */}
            <div className={styles.teamRow}>
              <div className={styles.teamInfo}>
                <div className={styles.teamName}>
                  {game.away_team.rank && <span className={styles.rank}>#{game.away_team.rank}</span>}
                  <span>{game.away_team.name}</span>
                </div>
                <div className={styles.teamDetails}>
                  {game.away_team.record && <span className={styles.record}>{game.away_team.record}</span>}
                  {game.away_team.conference && <span className={styles.conference}>{game.away_team.conference}</span>}
                </div>
              </div>
              {game.game_status !== 'scheduled' && (
                <div className={styles.scoreDisplay}>
                  {game.away_score || 0}
                </div>
              )}
            </div>

            {/* Home Team */}
            <div className={styles.teamRow}>
              <div className={styles.teamInfo}>
                <div className={styles.teamName}>
                  {game.home_team.rank && <span className={styles.rank}>#{game.home_team.rank}</span>}
                  <span>{game.home_team.name}</span>
                </div>
                <div className={styles.teamDetails}>
                  {game.home_team.record && <span className={styles.record}>{game.home_team.record}</span>}
                  {game.home_team.conference && <span className={styles.conference}>{game.home_team.conference}</span>}
                </div>
              </div>
              {game.game_status !== 'scheduled' && (
                <div className={styles.scoreDisplay}>
                  {game.home_score || 0}
                </div>
              )}
            </div>

            {/* Game Type Indicators */}
            <div className={styles.gameTypeInfo}>
              {Boolean(game.conference_game) && <span className={styles.conferenceGame}>Conference Game</span>}
              {Boolean(game.neutral_site) && <span className={styles.neutralSite}>Neutral Site</span>}
              {game.attendance && game.attendance > 0 && <span className={styles.attendance}>Attendance: {game.attendance.toLocaleString()}</span>}
            </div>
          </div>

                  {/* Only show pick options if game is lockable */}
          {isGameLockable && (
            <div className={styles.pickOptions}>
              <h3>Pick Options</h3>
              {hasReachedMaxPicks && (
                <div className={styles.maxPicksWarning}>
                  You have reached the maximum number of picks for this week.
                </div>
              )}
              <div className={styles.pickGrid}>
                <button
                  className={`${styles.pickButton} ${selectedPick?.pickType === 'away' ? styles.selected : ''} ${hasReachedMaxPicks || isPickLockedByOther('away') || !game.betting.spread ? styles.disabled : ''}`}
                  onClick={() => !hasReachedMaxPicks && !isPickLockedByOther('away') && game.betting.spread && handlePickSelect('away', -game.betting.spread)}
                  disabled={hasReachedMaxPicks || isPickLockedByOther('away') || !game.betting.spread}
                >
                  <span className={styles.teamName}>
                    {game.away_team.rank && <span className={styles.rank}>#{game.away_team.rank}</span>}
                    {game.away_team.abbreviation || game.away_team.name}
                  </span>
                  <span className={styles.line}>{formatBettingLine(-game.betting.spread)}</span>
                  {isPickLockedByOther('away') && <span className={styles.lockedIndicator}>LOCKED</span>}
                </button>
                <button
                  className={`${styles.pickButton} ${selectedPick?.pickType === 'home' ? styles.selected : ''} ${hasReachedMaxPicks || isPickLockedByOther('home') || !game.betting.spread ? styles.disabled : ''}`}
                  onClick={() => !hasReachedMaxPicks && !isPickLockedByOther('home') && game.betting.spread && handlePickSelect('home', game.betting.spread)}
                  disabled={hasReachedMaxPicks || isPickLockedByOther('home') || !game.betting.spread}
                >
                  <span className={styles.teamName}>
                    {game.home_team.abbreviation || game.home_team.name}
                    {game.home_team.rank && <span className={styles.rank}>#{game.home_team.rank}</span>}
                  </span>
                  <span className={styles.line}>{formatBettingLine(game.betting.spread)}</span>
                  {isPickLockedByOther('home') && <span className={styles.lockedIndicator}>LOCKED</span>}
                </button>
                <button
                  className={`${styles.pickButton} ${selectedPick?.pickType === 'over' ? styles.selected : ''} ${hasReachedMaxPicks || isPickLockedByOther('over') || !game.betting.over_under ? styles.disabled : ''}`}
                  onClick={() => !hasReachedMaxPicks && !isPickLockedByOther('over') && game.betting.over_under && handlePickSelect('over', game.betting.over_under)}
                  disabled={hasReachedMaxPicks || isPickLockedByOther('over') || !game.betting.over_under}
                >
                  <span className={styles.teamName}>Over</span>
                  <span className={styles.line}>{formatBettingLine(game.betting.over_under)}</span>
                  {isPickLockedByOther('over') && <span className={styles.lockedIndicator}>LOCKED</span>}
                </button>
                <button
                  className={`${styles.pickButton} ${selectedPick?.pickType === 'under' ? styles.selected : ''} ${hasReachedMaxPicks || isPickLockedByOther('under') || !game.betting.over_under ? styles.disabled : ''}`}
                  onClick={() => !hasReachedMaxPicks && !isPickLockedByOther('under') && game.betting.over_under && handlePickSelect('under', game.betting.over_under)}
                  disabled={hasReachedMaxPicks || isPickLockedByOther('under') || !game.betting.over_under}
                >
                  <span className={styles.teamName}>Under</span>
                  <span className={styles.line}>{formatBettingLine(game.betting.over_under)}</span>
                  {isPickLockedByOther('under') && <span className={styles.lockedIndicator}>LOCKED</span>}
                </button>
              </div>
            </div>
          )}

          {/* Show game status message for non-lockable games */}
          {!isGameLockable && (
            <div className={styles.gameStatusMessage}>
              <h3>Game Status</h3>
              <div className={styles.statusInfo}>
                {game.game_status === 'in_progress' && (
                  <div className={styles.inProgressStatus}>
                    <span className={styles.statusIcon}>⚽</span>
                    <span>This game is currently in progress</span>
                  </div>
                )}
                {game.game_status === 'completed' && (
                  <div className={styles.completedStatus}>
                    <span className={styles.statusIcon}>🏁</span>
                    <span>This game has been completed</span>
                  </div>
                )}
              </div>
            </div>
          )}

                  {/* Enhanced Lock Status Bars with Margins */}
          {gameLocksWithMargins.length > 0 && (canSeeLocks() || hasReachedMaxPicks) && (
            <div className={styles.lockStatusBars}>
              <h3>Lock Status</h3>
              {gameLocksWithMargins.map((lock) => {
                const pickLabel = getPickLabel(lock.pickType, game);
                const line = formatLockLine(lock.lockLine, lock.pickType === 'over' || lock.pickType === 'under');
                const marginClass = getMarginColor(lock.margin);

                // Determine what to show based on league settings and user state
                const isUserLock = lock.userId === user?.id;
                const showUsername = isUserLock || canSeeLockers(displayedWeek, isUserLock, 0, 1) || hasReachedMaxPicks;
                const showPickInfo = canSeeLocks() || hasReachedMaxPicks;

                // Determine status text based on game status and margin
                let statusText = '';
                if (game.game_status === 'completed') {
                  statusText = lock.won_lost ? lock.won_lost.charAt(0).toUpperCase() + lock.won_lost.slice(1) : 'Locked';
                } else if (game.game_status === 'in_progress') {
                  if (lock.margin > 0) {
                    statusText = `W'ing +${lock.margin.toFixed(1)}`;
                  } else if (lock.margin < 0) {
                    statusText = `L'ing ${lock.margin.toFixed(1)}`;
                  } else {
                    statusText = 'Push';
                  }
                } else {
                  statusText = 'Locked';
                }

                return (
                  <div
                    key={`${lock.userId}-${lock.pickType}`}
                    className={`${styles.lockStatusBar} ${marginClass}`}
                  >
                    <span className={styles.userSection}>
                      {showUsername ? (
                        lock.username
                      ) : (
                        <HiddenText
                          type="locker"
                          weekNumber={displayedWeek}
                          showTooltip={true}
                        />
                      )}
                    </span>
                    <div className={styles.pickSection}>
                      {showPickInfo ? (
                        <span>{pickLabel} {line}</span>
                      ) : (
                        <HiddenText
                          type="lock"
                          weekNumber={displayedWeek}
                          showTooltip={true}
                        />
                      )}
                      <span className={styles.statusBadge}>
                        {statusText}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

                                                 {/* Only show confirmation actions if game is lockable */}
                         {isGameLockable && selectedPick && (
                           <div className={styles.confirmationActions}>
                             <button
                               className={styles.watchButton}
                               onClick={() => handleConfirmPick('watch')}
                             >
                               Watch Pick
                             </button>
                             <button
                               className={`${styles.lockButton} ${isLockDisabled ? styles.disabled : ''}`}
                               onClick={() => handleConfirmPick('lock')}
                               disabled={isLockDisabled}
                             >
                               {isCheckingFutureWeek ? 'Checking...' : 'Lock Pick'}
                             </button>
                           </div>
                         )}
                         {isGameLockable && selectedPick && isFutureWeek && !futureWeekLockAllowed && (
                           <div className={styles.futureWeekMessage}>
                             Future week locks are disabled
                           </div>
                         )}
        </dialog>
      </div>
    </>
  );
};

MobileGameDetail.propTypes = {
  game: PropTypes.object.isRequired,
  displayedWeek: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPickAction: PropTypes.func.isRequired,
  hasReachedMaxPicks: PropTypes.bool
};

export default MobileGameDetail;
